import { useRef, useState } from 'react';

import {
	LockOutlined as LockOutlinedIcon,
	RoomOutlined as RoomOutlinedIcon,
} from '@mui/icons-material';
import { Button, Chip, Link, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { InfoCard, InfoCardRowDef, Tooltip } from '~components';
import { DeviceHeartbeatsService, ItemHeartbeatsService } from '~features/heartbeats';
import { ItemsInItemGroupService } from '~features/items';
import { useSnackbar } from '~hooks';
import { GeoCoordinate } from '~interfaces';
import { buildGoogleMapsPlaceUrl } from '~utils';
import { datetimeNoSecondsFormatter } from '~utils/dateUtils';

import { mapBatteryLevelEnum } from '../../../../shared/utility';
import DeviceTypeEnum from '../../enums/deviceTypeEnum';
import LockStateEnum from '../../enums/lockStateEnum';
import DevicesService from '../../services/devicesService';
import GeoLocationIcon from '../icons/geoLocationIcon';

const devicesService = new DevicesService();
const deviceHeartbeatsService = new DeviceHeartbeatsService();
const itemHeartbeatsService = new ItemHeartbeatsService();

type DeviceStatusCardProps = {
	deviceType: DeviceTypeEnum;
} & (
	| {
			deviceId: string;
			itemId?: never;
			itemGroupId?: never;
	  }
	| {
			deviceId?: never;
			itemId: string;
			itemGroupId: string;
	  }
);

/**
 * A card to show the status/heartbeat of a device.
 */
const DeviceStatusCard = ({ ...props }: DeviceStatusCardProps) => {
	const { t } = useTranslation();

	const itemsService = useRef<ItemsInItemGroupService | null>(
		props.itemGroupId ? new ItemsInItemGroupService(props.itemGroupId) : null
	);

	const { enqueueErrorSnackbar, enqueueAxiosErrorSnackbar } = useSnackbar();

	const [initialDeviceLocation, setInitialDeviceLocation] = useState(false);
	const [initialUnlockcode, setInitialUnlockcode] = useState(false);

	const isBoat = props.deviceType === DeviceTypeEnum.CpacBoat;
	const isBmw = props.deviceType === DeviceTypeEnum.Bmw;
	const isCar =
		props.deviceType === DeviceTypeEnum.VehicleTracker ||
		props.deviceType === DeviceTypeEnum.VehicleTrackerCan ||
		props.deviceType === DeviceTypeEnum.VehicleTrackerObd ||
		props.deviceType === DeviceTypeEnum.VehicleTrackerPro ||
		isBmw;

	/**
	 * Notice: at the moment of writing the bmw responses will always have an
	 * empty body, because it is deprecated
	 */
	const { data: heartbeatDeviceData, isLoading: isHeartbeatDeviceLoading } = useSWR(
		props.deviceId ?
			[
				deviceHeartbeatsService.basePath,
				props.deviceId,
				deviceHeartbeatsService.latestHeartbeatSubPath,
			]
		:	null,
		([_, id]) =>
			isBmw ?
				deviceHeartbeatsService.getBmwVehicleStatus(id)
			:	deviceHeartbeatsService.getLatestHeartbeat(id)
	);

	const { data: hearbeatItemData, isLoading: isHeartbeatItemLoading } = useSWR(
		props.itemId && props.itemGroupId ?
			[
				itemHeartbeatsService.basePath,
				props.itemGroupId,
				itemHeartbeatsService.itemsSubpath,
				props.itemId,
				itemHeartbeatsService.latestHeartbeatSubPath,
			]
		:	null,
		([_1, itemGroupId, _2, itemId]) => itemHeartbeatsService.getLatestHeartbeat(itemId, itemGroupId)
	);

	const heartbeatData = heartbeatDeviceData ?? hearbeatItemData;
	const isHeartbeatLoading = isHeartbeatDeviceLoading ?? isHeartbeatItemLoading;
	const deviceId = props.deviceId || hearbeatItemData?.deviceId;

	const { data: deviceData } = useSWR(
		props.deviceId ? [devicesService.basePath, props.deviceId] : null,
		([_, id]) => devicesService.getDeviceById(id)
	);

	const {
		data: deviceLocationData,
		isLoading: isDeviceLocationLoading,
		isValidating: isDeviceLocationValidating,
		mutate: mutateLocation,
	} = useSWR(
		initialDeviceLocation && deviceId ? [devicesService.basePath, deviceId, 'location'] : null,
		([_, id]) => devicesService.getDeviceLocation(id),
		{
			onSuccess: (data) => {
				if (data == null) {
					enqueueErrorSnackbar(t('ui.label.noLocationFound'));
				}
			},
			onError: (error) => enqueueAxiosErrorSnackbar(error, error.response?.data?.message),
		}
	);

	const { data: deviceUnlockCodeData, isLoading: isDeviceUnlockCodeLoading } = useSWR(
		initialUnlockcode && deviceId ? [devicesService.basePath, deviceId, 'unlockcode'] : null,
		([_, id]) => devicesService.getDeviceUnlockCode(id)
	);

	// Should maybe moved outside this card
	const { data: itemAccessData } = useSWR(
		props.itemId && props.itemGroupId && itemsService.current ?
			[
				itemsService.current.basePath,
				props.itemGroupId,
				itemsService.current.itemsSubPath,
				props.itemId,
				itemsService.current.itemAccessSubPath,
			]
		:	null,
		([_1, _2, _3, itemId]) => itemsService.current?.getAccess(itemId)
	);

	const access = itemAccessData?.includes('internal') ?? true;
	const showLocation =
		!access &&
		(deviceData?.communicationType.includes('lte') ||
			deviceData?.communicationType.includes('thread'));

	const handleGetLocationClick = () => {
		if (!initialDeviceLocation) {
			setInitialDeviceLocation(true);
		} else {
			mutateLocation();
		}
	};

	const rows: InfoCardRowDef[] = [
		{
			headerName: t(isCar ? 'views.ticketsDetails.lastUpdated' : 'ui.label.lastOnline'),
			value: heartbeatData?.dateUpdated,
			renderCell: (value?: Date) =>
				value ? <Chip color='info' label={datetimeNoSecondsFormatter.format(value)} /> : '-',
		},
		{
			headerName: t('views.devices.heartbeatsList.deviceState'),
			value: heartbeatData?.state,
			valueFormatter: (value?: string) => (value ? t(`ui.label.device.state.${value}`) : '-'),
			hidden: isCar,
		},
		{
			headerName: t('ui.label.lockStatus'),
			value: heartbeatData?.lockState,
			valueFormatter: (value?: LockStateEnum) =>
				value ?
					t(value === LockStateEnum.Secured ? 'ui.label.lockedSecurely' : `ui.label.${value}`)
				:	'-',
		},
		{
			headerName: t('ui.label.ignitionStatus'),
			value: heartbeatData?.ignitionDisabled,
			valueFormatter: (value?: boolean) =>
				value != null ? t(value ? 'ui.disabled' : 'ui.enabled') : '-',
			hidden: !(isBoat || isCar),
		},
		{
			headerName: t('ui.label.immobiliserStatus'),
			value: heartbeatData?.immobiliserEnabled,
			valueFormatter: (value?: boolean) =>
				value != null ? t(value ? 'ui.enabled' : 'ui.disabled') : '-',
			hidden: isBmw || !(isBoat || isCar),
		},
		{
			headerName: t('ui.label.keyDetected'),
			value: heartbeatData?.keyDetected,
			// TODO: for obvious reasons change translation key
			valueFormatter: (value: boolean) => (value ? t('ui.true') : '-'),
			hidden: isBmw || !isCar,
		},
		{
			headerName: t('ui.label.doorStatus'),
			value:
				heartbeatData?.doorsState != null ?
					heartbeatData.doorsState.reduce((result, status) => result || status.open, false)
				:	undefined,
			renderCell: (value?: boolean) => {
				if (value === undefined) {
					return '-';
				}

				return value ?
						<Tooltip
							title={heartbeatData?.doorsState
								.filter((el) => el.open)
								.map((el, i) => (
									<Typography key={i}>{`${t(`ui.label.${el.location}`)}`}</Typography>
								))}
						>
							{t('ui.open')}
						</Tooltip>
					:	t('ui.openStatus.closed');
			},
			hidden: !isCar,
		},
		{
			headerName: t('ui.label.windowStatus'),
			value:
				heartbeatData?.windowsState != null ?
					heartbeatData?.windowsState?.reduce((result, status) => result || status.open, false)
				:	undefined,
			renderCell: (value?: boolean) => {
				if (value === undefined) {
					return '-';
				}

				return value ?
						<Tooltip
							title={heartbeatData?.windowsState
								?.filter((el) => el.open)
								.map((el, i) => (
									<Typography key={i}>{`${t(`ui.label.${el.location}`)}`}</Typography>
								))}
						>
							<Typography>{t('ui.open')}</Typography>
						</Tooltip>
					:	t('ui.openStatus.closed');
			},
			hidden: !isBmw,
		},
		{
			headerName: t('ui.label.chargingStatus'),
			value: heartbeatData?.batteryIsCharging,
			valueFormatter: (value: boolean) => (value ? t('ui.label.device.state.charging') : '-'),
			hidden: !(isCar || isBoat),
		},
		{
			headerName: t('ui.label.battery'),
			value:
				props.deviceType === DeviceTypeEnum.DoorLock ?
					heartbeatData?.deviceBatteryLevelEnum
				:	heartbeatData?.deviceBatteryLevel,
			valueFormatter: (value?: number | string) => {
				if (typeof value === 'string') {
					return mapBatteryLevelEnum(value);
				}

				return value ? `${value}%` : '-';
			},
			hidden: isBoat,
		},
		{
			headerName: t('ui.label.secondaryBattery'),
			value: heartbeatData?.externalBatteryLevel,
			hidden: props.deviceType !== DeviceTypeEnum.TrailerLock,
		},
		{
			headerName: t('ui.label.vehicleBattery'),
			value: heartbeatData?.vehicleBatteryLevel,
			valueFormatter: (value: number) => (value ? `${value}%` : '-'),
			hidden: !(isCar || isBoat),
		},
		{
			headerName: t('ui.label.fuelLevelBMW'),
			value: heartbeatData?.vehicleFuelVolume,
			valueFormatter: (value: number) => (value ? `${value} liter` : '-'),
			hidden: !(isCar || isBoat),
		},
		{
			headerName: t('ui.device.label.reachCombustion'),
			value: heartbeatData?.rangeCombustion,
			valueFormatter: (value: number) => value ?? '-',
			hidden: !(isBmw || isBoat),
		},
		{
			headerName: t('ui.device.label.reachElectric'),
			value: heartbeatData?.rangeElectric,
			valueFormatter: (value: number) => value ?? '-',
			hidden: !(isCar || isBoat),
		},
		{
			headerName: t('ui.device.label.reachTotal'),
			value: heartbeatData?.range,
			valueFormatter: (value: number) => value || '-',
			hidden: !(isCar || isBoat),
		},
		{
			headerName: t('ui.label.mileage'),
			value: heartbeatData?.mileage,
			valueFormatter: (value?: number) => (value ? Math.floor(value) : '-'),
			hidden: !(isCar && access),
		},
		{
			headerName: t('ui.label.temperature'),
			value: heartbeatData?.temperature,
			valueFormatter: (value?: number) => (value ? `${value}˚C` : '-'),
			hidden: isCar,
		},
		{
			headerName: t('ui.label.firmwareVersion'),
			value: heartbeatData?.firmwareVersion,
			hidden: isCar || isBoat,
		},
		{
			headerName: t('ui.label.location'),
			value: heartbeatData?.location ?? deviceLocationData,
			renderCell: (value?: GeoCoordinate) =>
				value ?
					<Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
						<Link rel='noreferrer noopener' target='_blank' href={buildGoogleMapsPlaceUrl(value)}>
							{`${value.latitude}, ${value.longitude}`}
						</Link>
						{heartbeatData != null &&
							heartbeatData?.locationType != 'none' &&
							!isCar &&
							!isBoat && (
								<Tooltip
									placement='bottom'
									title={t(`views.devices.heartbeatsList.icon.${heartbeatData?.locationType}`)}
								>
									<GeoLocationIcon locationType={heartbeatData.locationType} />
								</Tooltip>
							)}
					</Stack>
				:	<Button
						startIcon={<RoomOutlinedIcon />}
						onClick={handleGetLocationClick}
						loadingPosition='start'
						loading={isDeviceLocationLoading || isDeviceLocationValidating}
					>
						{t('ui.label.getGps')}
					</Button>,
			hidden: !(showLocation || access || props.deviceType === DeviceTypeEnum.DoorLock),
		},
		{
			headerName: t('ui.label.unlockCode'),
			value: deviceUnlockCodeData?.code,
			renderCell: (value?: number) =>
				value ?? (
					<Button
						startIcon={<LockOutlinedIcon />}
						onClick={() => setInitialUnlockcode(true)}
						loadingPosition='start'
						loading={isDeviceUnlockCodeLoading}
					>
						{t('ui.button.contained.getUnlockCode')}
					</Button>
				),
			hidden: !access || !deviceData?.identificationType.includes('totp'), // || TODO: identificationTypes
		},
		{
			headerName: t('views.devices.details.deviceStatus.unlockCodeValid'),
			value: deviceUnlockCodeData?.adjustedEndDate,
			valueFormatter: (value?: Date) => datetimeNoSecondsFormatter.format(value),
			hidden: deviceUnlockCodeData == null,
		},
	];

	return (
		<InfoCard
			title={t('ui.label.deviceStatus')}
			rows={rows}
			loading={isHeartbeatLoading}
			version='legacy'
		/>
	);
};

export default DeviceStatusCard;
